import React, { useEffect, useState, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, setAuthPersistence } from '../firebase';
import { saveProfile, subscribeToProfile } from '../services/profileService';
import AddressPicker from './AddressPicker';
import { useMobileBack, OVERLAY_PRIORITY } from '../navigation';

function AuthPanel({ onAuthReady, isModal = false, onClose }) {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [user, setUser] = useState(null);
  const [rememberMe, setRememberMe] = useState(true);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('phone');
  const [isNewUser, setIsNewUser] = useState(false);

  const [name, setName] = useState('');
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [savedAddress, setSavedAddress] = useState(null);

  const uniqueId = useId();
  const recaptchaContainerId = `recaptcha-container-${uniqueId.replace(/:/g, '')}`;

  // Handle mobile back button for auth modal
  const handleAuthModalClose = useCallback(() => { if (onClose) onClose(); }, [onClose]);
  
  useMobileBack({
    id: 'auth-modal',
    priority: OVERLAY_PRIORITY.AUTH_MODAL,
    isOpen: isModal && Boolean(onClose),
    onClose: handleAuthModalClose,
  });

  const handleAuthReadyCallback = useCallback((currentUser) => {
    if (onAuthReady) onAuthReady(currentUser);
  }, [onAuthReady]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        handleAuthReadyCallback(currentUser);
      }
      if (currentUser && !isNewUser) {
        subscribeToProfile(currentUser.uid, (data) => {
          if (data && data.name) {
            handleAuthReadyCallback(currentUser);
          } else if (isModal) {
            setStep('profile');
            setIsNewUser(true);
          } else {
            handleAuthReadyCallback(currentUser);
          }
        });
      }
    });
    return () => unsub();
  }, [handleAuthReadyCallback, isNewUser, isModal]);

  const ensureRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (e) {}
      window.recaptchaVerifier = null;
    }
    window.recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'invisible' });
    return window.recaptchaVerifier;
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length !== 10) {
      setStatus('Please enter a valid 10-digit phone number.');
      return;
    }
    try {
      setStatus('');
      setIsLoading(true);
      await setAuthPersistence(rememberMe ? 'local' : 'session');
      const verifier = ensureRecaptcha();
      const confirmationResult = await signInWithPhoneNumber(auth, `+91${phone}`, verifier);
      setConfirmation(confirmationResult);
      setStatus('OTP sent! Check your phone.');
    } catch (error) {
      setStatus(error.message || 'Failed to send OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setStatus('');
      setIsLoading(true);
      if (!confirmation) { setStatus('Please request OTP first.'); return; }
      const result = await confirmation.confirm(otp);
      const isNew = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      if (isNew || isModal) {
        setIsNewUser(true);
        setStep('profile');
        setStatus('');
      } else {
        setStatus('Logged in successfully!');
        if (isModal && onClose) setTimeout(() => onClose(), 1000);
      }
    } catch (error) {
      setStatus(error.message || 'Invalid OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setConfirmation(null);
    setOtp('');
    setPhone('');
    setStep('phone');
    setIsNewUser(false);
    setName('');
    setSavedAddress(null);
    setShowAddressPicker(false);
    setStatus('');
  };

  const handlePhoneChange = (e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));

  const handleAddressPickerSave = (addressData) => {
    setSavedAddress(addressData);
    setShowAddressPicker(false);
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) { setStatus('Please enter your name.'); return; }
    if (!savedAddress) { setStatus('Please add a delivery address.'); return; }
    try {
      setIsLoading(true);
      setStatus('');
      await saveProfile(user.uid, {
        name: name.trim(),
        phone: user.phoneNumber,
        addresses: [savedAddress],
      });
      setStatus('Profile saved!');
      handleAuthReadyCallback(user);
      if (isModal && onClose) setTimeout(() => onClose(), 1000);
    } catch (error) {
      setStatus(error.message || 'Failed to save profile.');
    } finally {
      setIsLoading(false);
    }
  };

  const isProfileValid = name.trim() && savedAddress !== null;

  // Logged-in, inline mode — render nothing visible
  if (user && !isModal && step !== 'profile') {
    return <div id={recaptchaContainerId} style={{ display: 'none' }} />;
  }

  // Logged-in modal — profile completion or already signed in
  if (user && isModal) {
    if (step === 'profile') {
      return (
        <div className="auth-modal-overlay">
          <div className="auth-modal auth-modal-wide">
            <button className="auth-close-btn" type="button" onClick={onClose}>✕</button>
            <div className="auth-header">
              <div className="auth-logo">👤</div>
              <h2>Complete Your Profile</h2>
              <p className="muted">Add your details to get started</p>
            </div>
            <div className="auth-form profile-form">
              <label className="form-label">
                Full Name *
                <input type="text" placeholder="Enter your name" value={name} onChange={(e) => setName(e.target.value)} className="form-input" />
              </label>

              {/* Address section */}
              <div className="form-section">
                <h4>Delivery Address</h4>
                {savedAddress ? (
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                    borderRadius: 12, padding: '12px 14px',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46', marginBottom: 4 }}>
                        ✓ {savedAddress.label} — {savedAddress.line1}
                      </div>
                      <div style={{ fontSize: 12, color: '#15803d' }}>
                        {[savedAddress.location, savedAddress.city, savedAddress.pincode].filter(Boolean).join(', ')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddressPicker(true)}
                      style={{ background: 'none', border: 'none', color: '#15803d', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAddressPicker(true)}
                    style={{
                      width: '100%', padding: '14px',
                      borderRadius: 12, border: '1.5px dashed #ff5a1f',
                      background: '#fff4ed', color: '#ff5a1f',
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    📍 Add Delivery Address
                  </button>
                )}
              </div>

              <button className="btn primary full-width" type="button" onClick={handleSaveProfile} disabled={isLoading || !isProfileValid}>
                {isLoading ? 'Saving...' : 'Save & Continue'}
              </button>
              {status && (
                <p className={`auth-status ${status.includes('saved') ? 'success' : 'error'}`}>{status}</p>
              )}
            </div>
            <div id={recaptchaContainerId} />

            {/* AddressPicker modal */}
            {showAddressPicker && (
              <AddressPicker
                initialAddress={savedAddress || undefined}
                onSave={handleAddressPickerSave}
                onClose={() => setShowAddressPicker(false)}
              />
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="auth-modal-overlay">
        <div className="auth-modal">
          <button className="auth-close-btn" type="button" onClick={onClose}>✕</button>
          <div className="auth-success">
            <div className="success-icon">✓</div>
            <h2>You're signed in!</h2>
            <p className="muted">Logged in as {user.phoneNumber}</p>
            <button className="btn ghost" type="button" onClick={handleLogout}>Log out</button>
          </div>
          <div id={recaptchaContainerId} />
        </div>
      </div>
    );
  }

  // Inline auth section (for homepage)
  if (!isModal) {
    return (
      <div style={{
        margin: '0 12px 12px',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(124,58,237,0.10)',
        border: '1px solid rgba(124,58,237,0.12)',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, var(--primary, #7c3aed) 0%, #5b21b6 100%)',
          padding: '16px 16px 14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontSize: '22px', lineHeight: 1 }}>⚡</span>
            <span style={{ fontSize: '17px', fontWeight: '800', color: 'white', letterSpacing: '-0.2px' }}>
              Groceries Delivered in 15–45 Minutes
            </span>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'rgba(255,255,255,0.82)', lineHeight: '1.4' }}>
            Fresh essentials from local stores, delivered fast across Gudalur
          </p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { icon: '🚚', text: 'Free delivery ₹149+' },
              { icon: '💵', text: 'Cash on Delivery' },
              { icon: '🏪', text: 'Local stores' },
            ].map(({ icon, text }) => (
              <span key={text} style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '20px',
                padding: '4px 10px',
                fontSize: '11px',
                fontWeight: '600',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                {icon} {text}
              </span>
            ))}
          </div>
        </div>
        <div id={recaptchaContainerId} style={{ display: 'none' }} />
      </div>
    );
  }

  // Modal auth — full-screen page (for cart checkout)
  return createPortal(
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 16px)',
      animation: 'slideUpModal 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
    }}>

      {/* Top bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #f3f4f6',
        flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: '#f3f4f6', border: 'none',
            borderRadius: '50%', width: '36px', height: '36px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#374151', fontSize: '18px',
          }}
        >←</button>
        <span style={{ marginLeft: '12px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
          Sign in
        </span>
      </div>

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary, #ff5a1f) 0%, #c2410c 100%)',
        padding: '32px 24px 36px',
        textAlign: 'center',
        flexShrink: 0,
      }}>
        <div style={{ fontSize: '44px', marginBottom: '12px', lineHeight: 1 }}>⚡</div>
        <h2 style={{ margin: '0 0 8px', color: 'white', fontSize: '22px', fontWeight: '800', letterSpacing: '-0.3px' }}>
          Sign in to Continue
        </h2>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.5' }}>
          {confirmation
            ? `OTP sent to +91 ${phone}`
            : 'Enter your phone number to sign in or create an account'}
        </p>
      </div>

      {/* Form */}
      <div style={{ padding: '28px 20px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
        {!confirmation ? (
          <>
            <div>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Phone Number
              </p>
              <div style={{
                display: 'flex',
                border: '1.5px solid #e5e7eb',
                borderRadius: '14px',
                overflow: 'hidden',
                background: 'white',
              }}>
                <span style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '0 14px', background: '#f9fafb',
                  borderRight: '1.5px solid #e5e7eb',
                  fontSize: '14px', fontWeight: '700', color: '#374151',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>🇮🇳 +91</span>
                <input
                  type="tel"
                  placeholder="Enter 10-digit number"
                  value={phone}
                  onChange={handlePhoneChange}
                  maxLength={10}
                  autoFocus
                  style={{
                    flex: 1, border: 'none', outline: 'none',
                    padding: '16px 14px', fontSize: '16px',
                    background: 'white', color: '#111827', minWidth: 0,
                    fontWeight: '500',
                  }}
                />
                {phone.length === 10 && (
                  <span style={{ display: 'flex', alignItems: 'center', paddingRight: '14px', color: '#10b981', fontSize: '20px' }}>✓</span>
                )}
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#6b7280', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: 'var(--primary, #ff5a1f)', width: '18px', height: '18px', flexShrink: 0 }}
              />
              Keep me signed in on this device
            </label>

            <button
              type="button"
              onClick={handleSendOtp}
              disabled={isLoading || phone.length !== 10}
              style={{
                width: '100%',
                background: phone.length === 10
                  ? 'linear-gradient(135deg, var(--primary, #ff5a1f) 0%, #c2410c 100%)'
                  : '#f3f4f6',
                color: phone.length === 10 ? 'white' : '#9ca3af',
                border: 'none', borderRadius: '14px',
                padding: '17px', fontSize: '16px', fontWeight: '700',
                cursor: phone.length === 10 ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                boxShadow: phone.length === 10 ? '0 4px 16px rgba(255,90,31,0.35)' : 'none',
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <span className="spinner-small" />
                  Sending OTP…
                </span>
              ) : 'Send OTP →'}
            </button>

            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', textAlign: 'center', lineHeight: '1.5' }}>
              By continuing, you agree to our Terms &amp; Privacy Policy
            </p>
          </>
        ) : (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', background: '#f0fdf4',
              borderRadius: '12px', border: '1px solid #bbf7d0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>✅</span>
                <span style={{ fontSize: '13px', color: '#15803d', fontWeight: '600' }}>OTP sent to +91 {phone}</span>
              </div>
              <button
                type="button"
                onClick={() => { setConfirmation(null); setOtp(''); setStatus(''); }}
                style={{ background: 'none', border: 'none', color: '#15803d', fontSize: '13px', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >Change</button>
            </div>

            <div>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Enter OTP
              </p>
              <input
                type="text"
                inputMode="numeric"
                placeholder="——————"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                style={{
                  width: '100%', borderRadius: '14px',
                  padding: '18px', fontSize: '32px', fontWeight: '800',
                  letterSpacing: '16px', outline: 'none', textAlign: 'center',
                  color: '#111827', background: 'white', boxSizing: 'border-box',
                  border: `1.5px solid ${otp.length === 6 ? '#10b981' : '#e5e7eb'}`,
                  transition: 'border-color 0.2s',
                }}
              />
            </div>

            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.length !== 6}
              style={{
                width: '100%',
                background: otp.length === 6
                  ? 'linear-gradient(135deg, var(--primary, #ff5a1f) 0%, #c2410c 100%)'
                  : '#f3f4f6',
                color: otp.length === 6 ? 'white' : '#9ca3af',
                border: 'none', borderRadius: '14px',
                padding: '17px', fontSize: '16px', fontWeight: '700',
                cursor: otp.length === 6 ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                boxShadow: otp.length === 6 ? '0 4px 16px rgba(255,90,31,0.35)' : 'none',
              }}
            >
              {isLoading ? 'Verifying…' : 'Verify & Continue →'}
            </button>
          </>
        )}

        {status && (
          <div style={{
            padding: '12px 16px', borderRadius: '10px',
            fontSize: '13px', fontWeight: '500', textAlign: 'center',
            background: status.toLowerCase().includes('sent') || status.toLowerCase().includes('success') ? '#f0fdf4' : '#fef2f2',
            color: status.toLowerCase().includes('sent') || status.toLowerCase().includes('success') ? '#15803d' : '#dc2626',
            border: `1px solid ${status.toLowerCase().includes('sent') || status.toLowerCase().includes('success') ? '#bbf7d0' : '#fecaca'}`,
          }}>{status}</div>
        )}
      </div>

      <div id={recaptchaContainerId} style={{ display: 'none' }} />

      <style>{`
        @keyframes slideUpModal { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>,
    document.body
  );
}

export default AuthPanel;
